import React, { useState, useRef, useEffect } from 'react';

interface CarouselItem {
  content: React.ReactNode;
  preview: {
    title: string;
    subtitle?: string;
  };
}

interface CarouselProps {
  items: CarouselItem[];
}

const Carousel: React.FC<CarouselProps> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const scrollToIndex = (index: number) => {
    setCurrentIndex(index);
  };

  const handlePrev = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    scrollToIndex(newIndex);
  };

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      // Swiped left
      handleNext();
    }

    if (touchStartX.current - touchEndX.current < -50) {
      // Swiped right
      handlePrev();
    }
  };

  if (items.length === 0) return null;

  const renderPreview = (item: CarouselItem, onClick?: () => void) => (
    <div 
      className="card" 
      onClick={onClick}
      style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: 'var(--space-xl)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <h4 style={{ 
        fontSize: '1.1rem', 
        marginBottom: item.preview.subtitle ? 'var(--space-sm)' : 0,
        lineHeight: 1.3,
      }}>
        {item.preview.title}
      </h4>
      {item.preview.subtitle && (
        <p style={{ 
          fontSize: '0.9rem', 
          color: 'var(--color-text-muted)',
          margin: 0,
        }}>
          {item.preview.subtitle}
        </p>
      )}
    </div>
  );

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <div
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: '1rem',
          padding: '2rem 0',
          minHeight: '400px',
        }}
      >
        {/* Previous item (faded preview) */}
        {items.length > 1 && (
          <div className="carousel-side-item">
            {renderPreview(items[currentIndex > 0 ? currentIndex - 1 : items.length - 1], handlePrev)}
          </div>
        )}

        {/* Current item (center, full content) */}
        <div className="carousel-center-item">
          {items[currentIndex].content}
        </div>

        {/* Next item (faded preview) */}
        {items.length > 1 && (
          <div className="carousel-side-item">
            {renderPreview(items[currentIndex < items.length - 1 ? currentIndex + 1 : 0], handleNext)}
          </div>
        )}
      </div>

      {/* Navigation Buttons - Only visible on mobile */}
      {items.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="carousel-button carousel-button-prev"
            aria-label="Previous item"
          >
            <span style={{ fontSize: '2.5rem', color: 'var(--color-primary)', lineHeight: 1 }}>‹</span>
          </button>
          <button
            onClick={handleNext}
            className="carousel-button carousel-button-next"
            aria-label="Next item"
          >
            <span style={{ fontSize: '2.5rem', color: 'var(--color-primary)', lineHeight: 1 }}>›</span>
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {items.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              style={{
                width: index === currentIndex ? '32px' : '10px',
                height: '10px',
                borderRadius: '5px',
                border: 'none',
                background: index === currentIndex ? 'var(--color-primary)' : 'var(--color-border)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
